import { Check, Moon, Sun } from "lucide-react";
import { DARK_VARIANTS, LIGHT_VARIANTS, useTheme } from "@/lib/theme";
import { DARK_PREVIEW, LIGHT_PREVIEW } from "./appearance.constants";

export function AppearanceTab() {
  const { theme, toggleTheme, darkVariant, setDarkVariant, lightVariant, setLightVariant } =
    useTheme();
  const isDark = theme === "dark";

  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-border bg-card p-4">
        <p className="mb-1 text-[11.5px] font-semibold text-foreground">Mode</p>
        <p className="mb-3 text-[11.5px] text-muted-foreground">
          Réglage local à cet appareil, pas synchronisé entre vos différents écrans.
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => isDark && toggleTheme()}
            className={`flex items-center gap-1.5 rounded border px-3 py-2 text-[12px] font-medium transition-colors ${
              !isDark
                ? "border-brand-gold-dark bg-brand-gold-dark/10 text-brand-gold-dark"
                : "border-border text-muted-foreground hover:text-body"
            }`}
          >
            <Sun size={14} /> Clair
          </button>
          <button
            type="button"
            onClick={() => !isDark && toggleTheme()}
            className={`flex items-center gap-1.5 rounded border px-3 py-2 text-[12px] font-medium transition-colors ${
              isDark
                ? "border-brand-gold-dark bg-brand-gold-dark/10 text-brand-gold-dark"
                : "border-border text-muted-foreground hover:text-body"
            }`}
          >
            <Moon size={14} /> Sombre
          </button>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card p-4">
        <p className="mb-1 text-[11.5px] font-semibold text-foreground">Palette</p>
        <p className="mb-3 text-[11.5px] text-muted-foreground">
          S'applique immédiatement, indépendamment pour chaque mode. Changer de palette en mode{" "}
          {isDark ? "clair" : "sombre"} ne change rien ici.
        </p>
        <div className="grid grid-cols-3 gap-3">
          {isDark
            ? DARK_VARIANTS.map((variant) => (
                <VariantSwatch
                  key={variant.value}
                  label={variant.label}
                  preview={DARK_PREVIEW[variant.value]}
                  active={darkVariant === variant.value}
                  onClick={() => setDarkVariant(variant.value)}
                />
              ))
            : LIGHT_VARIANTS.map((variant) => (
                <VariantSwatch
                  key={variant.value}
                  label={variant.label}
                  preview={LIGHT_PREVIEW[variant.value]}
                  active={lightVariant === variant.value}
                  onClick={() => setLightVariant(variant.value)}
                />
              ))}
        </div>
      </div>
    </div>
  );
}

function VariantSwatch({
  label,
  preview,
  active,
  onClick,
}: {
  label: string;
  preview: { bg: string; card: string; accent: string };
  active: boolean;
  onClick: () => void;
}) {
  const textColor = preview.bg === "#ffffff" ? "#171717" : "#ffffff";
  const mutedTextColor = preview.bg === "#ffffff" ? "#737373" : "rgba(255,255,255,0.6)";

  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative rounded-lg border p-2.5 text-left transition-colors ${
        active ? "border-brand-gold-dark" : "border-border hover:border-muted-foreground"
      }`}
      style={{ background: preview.bg }}
    >
      {active && (
        <span className="absolute right-2 top-2 flex h-4 w-4 items-center justify-center rounded-full bg-brand-gold-dark text-white">
          <Check size={10} />
        </span>
      )}
      <div
        className="mb-2 rounded border p-2"
        style={{
          background: preview.card,
          borderColor: preview.bg === "#ffffff" ? "#e5e5e5" : "transparent",
        }}
      >
        <p className="text-[10.5px] font-medium" style={{ color: textColor }}>
          Proforma
        </p>
        <p className="text-[9.5px]" style={{ color: mutedTextColor }}>
          870 000 XAF
        </p>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: preview.accent }} />
        <p className="text-[10.5px] font-medium" style={{ color: textColor }}>
          {label}
        </p>
      </div>
    </button>
  );
}
